import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IframeContext } from "../../../context/IframeContext";
import { useDiaryContext } from "../../../hooks/useDiaryContext";
import { MainMenu } from "../../navigation/MainMenu";

import { useIntercomVisibility } from "../../../hooks/useIntercomVisibility";
import useNavBarItems from "../../../hooks/useNavBarItems";
import useWorkspaceContext from "../../../hooks/useWorkspaceContext";
import { dmnStorage } from "../../../utils/dmnStorage";
import { transformNavigationData } from "../../../utils/navigation/navigationTransformer";
import { SupportToggleButton } from "./SupportToggleButton/SupportToggleButton";

export const NavigationBarReplacement = () => {
  const [collapsed, setCollapsed] = useState<boolean>();
  const { venueConfig, selectedView, openedFromEnquiry } = useDiaryContext();
  const { bottomItems, topItems, loading, appBaseUrl } = useNavBarItems();
  const isInIframe = useContext(IframeContext);
  const {
    isVisible: isSupportVisible,
    toggleVisibility: toggleSupportVisibility
  } = useIntercomVisibility();

  // Get collapsed state from storage on mount
  useEffect(() => {
    (async () => {
      const storedCollapsed =
        (await dmnStorage.get("sideBarCollapsed")) ?? false;
      setCollapsed(storedCollapsed);
    })();
  }, []);

  // Save collapsed state to storage when it changes
  useEffect(() => {
    if (collapsed !== undefined) {
      dmnStorage.add("sideBarCollapsed", collapsed);
    }
  }, [collapsed]);

  // Transform navigation data for MainMenu - memoized to prevent unnecessary recalculations
  // When openedFromEnquiry is true, force the Enquiries tab to be active
  const navigationOptions = openedFromEnquiry
    ? { forceActiveItemId: "enquiries" }
    : undefined;

  const workspace = useWorkspaceContext(isInIframe);
  const navigate = useNavigate();

  const transformedTopItems = useMemo(() => {
    return transformNavigationData(
      topItems,
      location.pathname,
      venueConfig?._id,
      selectedView,
      navigationOptions,
      appBaseUrl,
      workspace,
      navigate
    );
  }, [
    topItems,
    location.pathname,
    venueConfig?._id,
    selectedView,
    navigationOptions,
    appBaseUrl,
    workspace,
    navigate
  ]);

  const transformedBottomItems = useMemo(() => {
    return transformNavigationData(
      bottomItems,
      location.pathname,
      venueConfig?._id,
      selectedView,
      navigationOptions,
      appBaseUrl,
      workspace,
      navigate
    );
  }, [
    bottomItems,
    location.pathname,
    venueConfig?._id,
    selectedView,
    navigationOptions,
    appBaseUrl,
    workspace,
    navigate
  ]);

  // Header content for MainMenu - memoized to prevent unnecessary re-renders
  const headerContent = useMemo(
    () => (
      <div className="NavigationBar__header w-full gap-[12vh] flex flex-row justify-between items-center">
        <span>Menu</span>
      </div>
    ),
    []
  );

  // Footer content with support toggle button
  const footerContent = useMemo(
    () => (
      <SupportToggleButton
        isVisible={isSupportVisible}
        onToggle={toggleSupportVisibility}
        collapsed={collapsed}
      />
    ),
    [isSupportVisible, toggleSupportVisibility, collapsed]
  );

  return (
    <div
      className={[
        "NavigationBar",
        `NavigationBar--${loading ? "loading" : "loaded"}`,
        "z-[13] h-full bg-[#464c60]"
      ].join(" ")}
    >
      <MainMenu
        loading={loading}
        items={transformedTopItems}
        bottomItems={transformedBottomItems} // Pass bottom items separately
        headerContent={headerContent}
        footerContent={footerContent}
        collapsible={true}
        collapsed={collapsed}
        onCollapseChange={setCollapsed}
        className="navigation-bar-replacement"
        currentVenueId={venueConfig?._id}
        appBaseUrl={appBaseUrl}
        workspace={workspace}
      />
    </div>
  );
};
